import { SagaIterator } from "redux-saga";
import { call, select, takeLatest } from "typed-redux-saga";
import { AnyAction } from "redux";
import { Action } from "typescript-fsa";

import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

import { assertNonNullable } from "../../../utilities/assert";
import { createXFinanzDatei, isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { ModulfDataSelectors } from "../../../utilities/selectors/modulfDataSelectors";
import { RESOURCE_KEYS } from "../../../localization";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { CREATE_XFINANZ_DATEI } from "../../../utilities/variables/customEventNames";
import { executeWithoutValidation, weakValidation } from "../../../utilities/validationUtils";
import {
	MAPPING_CONFIGURATION,
	PATH_TO_FILE_ATTACHMENT_LOCATION,
	XFANANZ_PATH
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, updateWorkflowsActivityData } from "../../../utilities/workflowUtils";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";

import Exception = JsonRpc2Response.Exception;

const XFINANZ_KEYS = RESOURCE_KEYS.features.xFinanzDatei;

const documentService = new DocumentServiceFactory().getDocumentService();

export function* createXFinanzDateiSaga(): SagaIterator<void> {
	yield* takeLatest(
		(action: AnyAction) => {
			return (
				FormEngineActions.event.match(action) &&
				Events.eventButton.match(action.payload.engineEvent) &&
				[CREATE_XFINANZ_DATEI].includes(action.payload.engineEvent.payload.name)
			);
		},
		function* (action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonPayload>>>) {
			yield* call(executeBusy, function* () {
				const { activityId } = action.payload;
				yield* call(executeWithoutValidation, activityId, function* () {
					const engineState = yield* select(FormEngineSelectors.engineState(activityId));
					assertNonNullable(engineState);
					const documentModel = ModelSelectors.documentModel()(engineState);
					const { buttonPath } = action.payload.engineEvent.payload;
					const annotations = yield* select(
						ModulfUiSelectors.selectButtonAnnotations(activityId, buttonPath)
					);

					// Pfad zum File-Upload-Feld aus der Button-Annotation lesen
					// Attachment path enthält vor root noch Namen des Models, muss entfernt werden
					let pathToFileAttachments = annotations.find(
						it => it.name === PATH_TO_FILE_ATTACHMENT_LOCATION
					)?.value;
					pathToFileAttachments = pathToFileAttachments?.substring(pathToFileAttachments?.indexOf("/root/"));

					let xfinanzGroupPath = annotations.find(it => it.name === XFANANZ_PATH)?.value;
					xfinanzGroupPath = xfinanzGroupPath?.substring(xfinanzGroupPath?.indexOf("/root/"));

					const mappingConfigurationId = annotations.find(it => it.name === MAPPING_CONFIGURATION)?.value;

					const data = yield* select(ModulfDataSelectors.selectTaskDocument(activityId));

					if (!(pathToFileAttachments && data && mappingConfigurationId && xfinanzGroupPath)) {
						yield* call(
							putErrorNotification,
							{ key: XFINANZ_KEYS.error.missingValue },
							{},
							activityId,
							"xFinanzDatei"
						);
						return;
					}
					const isValid = yield* call(weakValidation, activityId);
					if (!isValid) {
						return;
					}

					try {
						const updatedDocument = yield* call(
							createXFinanzDatei,
							documentService.formatDates(data, documentModel),
							data.id,
							pathToFileAttachments,
							xfinanzGroupPath,
							mappingConfigurationId
						);

						const parsedDocument = {
							...documentService.parseDates(updatedDocument, documentModel),
							modelId: documentModel.header.id,
							id: data.id
						};
						yield* call(updateWorkflowsActivityData, parsedDocument);
					} catch (error) {
						let message: Localizable;
						if (isJsonRpc2ResponseArray(error)) {
							message = extractExceptionMessage(error);
						} else {
							message = {
								key: XFINANZ_KEYS.error.internalServerError,
								args: { reason: { type: "plain", value: "Unbekannter Fehler." } }
							};
						}
						yield* call(putErrorNotification, message, error, activityId, "xFinanzDatei");
					}
				});
			});
		}
	);
}

function extractExceptionMessage(response: JsonRpc2Response[]): Localizable {
	const description = response
		.filter(JsonRpc2Response.error.isInstance)
		.map(it => it.error.data)
		.filter(Exception.isInstance)
		.map(it => it.description)
		.pop();
	return {
		key: XFINANZ_KEYS.error.internalServerError,
		args: { reason: { type: "plain", value: description?.default ?? "Unbekannter Fehler." } }
	};
}
